import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Wallet, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft,
  DollarSign,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { walletService, getLocalizedValue } from '@/services/api';

const WalletPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addAmount, setAddAmount] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchWalletData();
  }, [isAuthenticated, navigate]);

  const fetchWalletData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch wallet balance and transactions
      const [walletRes, transactionsRes] = await Promise.allSettled([
        walletService.getBalance(),
        walletService.getTransactions()
      ]);
      
      // Handle wallet balance
      if (walletRes.status === 'fulfilled' && walletRes.value?.data) {
        const walletData = walletRes.value.data.data || walletRes.value.data;
        // Wallet might be an array or object
        const wallet = Array.isArray(walletData) ? walletData[0] : walletData;
        setBalance(parseFloat(wallet?.balance || wallet?.total_balance || 0));
      }
      
      // Handle transactions
      if (transactionsRes.status === 'fulfilled' && transactionsRes.value?.data) {
        const transData = transactionsRes.value.data.data || transactionsRes.value.data || [];
        const mappedTransactions = Array.isArray(transData) ? transData.map(t => ({
          id: t.id,
          type: t.action || (t.amount > 0 ? 'credit' : 'debit'),
          amount: Math.abs(parseFloat(t.amount || 0)),
          description: getLocalizedValue(t.description, lang) || t.description || '',
          date: t.created_at ? new Date(t.created_at) : new Date(),
          status: t.status || 'completed',
        })) : [];
        setTransactions(mappedTransactions);
      }
    } catch (err) {
      console.error('Failed to fetch wallet data:', err);
      setError(lang === 'ar' ? 'فشل في تحميل بيانات المحفظة' : 'Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBalance = async () => {
    const amount = parseFloat(addAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(lang === 'ar' ? 'يرجى إدخال مبلغ صحيح' : 'Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      await walletService.addFunds({ amount });
      toast.success(lang === 'ar' ? 'تم إضافة الرصيد بنجاح' : 'Balance added successfully');
      setAddAmount('');
      setAddDialogOpen(false);
      // Refresh wallet data
      fetchWalletData();
    } catch (err) {
      console.error('Failed to add balance:', err);
      toast.error(lang === 'ar' ? 'فشل في إضافة الرصيد' : 'Failed to add balance');
    } finally {
      setSubmitting(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit':
      case 'add':
      case 'refund':
        return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
      case 'debit':
      case 'subtract':
        return <ArrowUpRight className="h-4 w-4 text-red-600" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'credit':
      case 'add':
      case 'refund':
        return 'text-green-600';
      case 'debit':
      case 'subtract':
        return 'text-red-600';
      default:
        return 'text-slate-600 dark:text-slate-400';
    }
  };

  const quickAmounts = [50, 100, 200, 500];

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="wallet-page">
      {/* Header with Balance */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Wallet className="h-8 w-8" />
              <h1 className="text-2xl font-bold">
                {lang === 'ar' ? 'المحفظة' : 'Wallet'}
              </h1>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={fetchWalletData}
              disabled={loading}
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <Card className="bg-white/10 border-0 backdrop-blur">
            <CardContent className="p-6">
              <p className="text-teal-100 mb-2">
                {lang === 'ar' ? 'الرصيد المتاح' : 'Available Balance'}
              </p>
              <div className="flex items-end justify-between">
                {loading ? (
                  <Skeleton className="h-10 w-32 bg-white/20" />
                ) : (
                  <h2 className="text-4xl font-bold" data-testid="wallet-balance">
                    ${balance.toFixed(2)}
                  </h2>
                )}
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-white text-teal-700 hover:bg-white/90" data-testid="add-balance-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      {lang === 'ar' ? 'إضافة رصيد' : 'Add Balance'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="dark:bg-slate-800">
                    <DialogHeader>
                      <DialogTitle className="dark:text-white">
                        {lang === 'ar' ? 'إضافة رصيد' : 'Add Balance'}
                      </DialogTitle>
                      <DialogDescription>
                        {lang === 'ar' 
                          ? 'أدخل المبلغ الذي تريد إضافته إلى محفظتك'
                          : 'Enter the amount you want to add to your wallet'
                        }
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="flex gap-2 flex-wrap">
                        {quickAmounts.map(amt => (
                          <Button
                            key={amt}
                            variant="outline"
                            size="sm"
                            onClick={() => setAddAmount(amt.toString())}
                            className={addAmount === amt.toString() ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/50' : ''}
                          >
                            ${amt}
                          </Button>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label className="dark:text-white">{lang === 'ar' ? 'المبلغ' : 'Amount'}</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            type="number"
                            placeholder="0.00"
                            value={addAmount}
                            onChange={(e) => setAddAmount(e.target.value)}
                            className="pl-10 dark:bg-slate-700 dark:text-white"
                            data-testid="amount-input"
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button 
                        className="bg-teal-600 hover:bg-teal-700" 
                        onClick={handleAddBalance}
                        disabled={submitting}
                        data-testid="confirm-add-btn"
                      >
                        {submitting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                        {lang === 'ar' ? 'إضافة' : 'Add'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Transactions */}
      <div className="container mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}
        
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg dark:text-white">
              {lang === 'ar' ? 'سجل المعاملات' : 'Transaction History'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8">
                <Wallet className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'لا توجد معاملات' : 'No transactions yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y dark:divide-slate-700">
                {transactions.map(transaction => (
                  <div 
                    key={transaction.id} 
                    className="flex items-center gap-4 py-4"
                    data-testid={`transaction-${transaction.id}`}
                  >
                    <div className={`p-2 rounded-full ${
                      transaction.type === 'credit' || transaction.type === 'add' || transaction.type === 'refund'
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : 'bg-red-100 dark:bg-red-900/30'
                    }`}>
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {transaction.description || (transaction.type === 'credit' ? 
                          (lang === 'ar' ? 'إضافة رصيد' : 'Balance Added') : 
                          (lang === 'ar' ? 'خصم' : 'Debit')
                        )}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {format(transaction.date, 'PPp', { locale })}
                      </p>
                    </div>
                    <span className={`font-semibold ${getTransactionColor(transaction.type)}`}>
                      {transaction.type === 'debit' || transaction.type === 'subtract' ? '-' : '+'}${transaction.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalletPage;
